<script setup>
import Button from "@/features/Button.vue";
import { computed } from "vue";
import InlineSvg from "@/features/InlineSvg.vue";
import { useDataDeletionRow } from "@/routes/DataDeletion/composables/useDataDeletionRow";
import BaseText from "@/library/BaseText.vue";

// Use our shared composable for common functionality
const {
  iframeRef,
  rowDetailsRef,
  state,
  renderMarkdown,
  resizeIframe,
  setupResizeWatch,
  isNameInSupportedList,
  onLinkToSupportPage,
} = useDataDeletionRow();

// Set up the iframe resize watcher
setupResizeWatch();

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  date: {
    type: String,
    required: true,
  },
  count: {
    type: Number,
    required: true,
  },
  opened: {
    type: Boolean,
    default: false,
  },
  brokers: {
    type: Array,
    default: () => [],
  },
  instructions: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["done", "toggleOpen"]);

// Use the shared renderMarkdown function
const markdownToHtml = computed(() => renderMarkdown(props.instructions));

const showSupportLink = computed(() => isNameInSupportedList(props.name));

function handleSupportLinkClick() {
  onLinkToSupportPage(props.name);
}
</script>
<template>
  <div
    class="action-row-wrapper"
    :class="{ opened: props.opened }"
    @click.stop="emit('toggleOpen')"
  >
    <div class="action-row">
      <div class="name action-row-item">
        <BaseText
          variant="headline-6-medium"
          class="icon"
        >
          {{ props.opened ? "-" : "+" }}
        </BaseText>
        <BaseText
          variant="headline-6-medium"
          class="text"
        >
          {{ `${props.name}${props.count ? " (" + props.count + ")" : ""}` }}
        </BaseText>
      </div>
      <div class="review-text action-row-item">
        <InlineSvg name="needs-attention" />
        <BaseText variant="headline-6-medium">Needs attention</BaseText>
      </div>
      <div class="done-button action-row-item">
        <Button
          type="secondary"
          size="md"
          class="long-button"
          @click.stop="emit('done')"
        >
          Mark as done
        </Button>
        <Button
          type="secondary"
          size="full"
          class="med-button"
          @click.stop="emit('done')"
        >
          Mark as done
        </Button>
        <Button
          type="secondary"
          size="full"
          class="short-button"
          @click.stop="emit('done')"
        >
          Done
        </Button>
      </div>
    </div>
    <div
      class="action-row-details-wrapper"
      :class="{ opened: props.opened }"
    >
      <div
        ref="rowDetailsRef"
        class="action-row-details"
        :class="{ opened: props.opened }"
      >
        <BaseText
          variant="headline-6-medium"
          class="warning"
        >
          {{ props.name }} requires some additional information from you in
          order to remove your records.
        </BaseText>
        <BaseText
          v-if="props.count"
          as="h3"
          variant="headline-4-bold"
        >
          Requesting to remove your info from {{ props.name }} will remove it
          from:
        </BaseText>
        <ol v-if="props.count">
          <li
            v-for="(broker, idx) in props.brokers"
            :key="`${broker?.name}-${idx}`"
          >
            <BaseText
              variant="caption-semibold"
              class="list-rank"
            >
              {{ idx + 1 }}
            </BaseText>
            <BaseText variant="headline-6-medium">{{ broker?.name }}</BaseText>
          </li>
        </ol>
        <BaseText
          as="h3"
          variant="headline-4-bold"
        >
          How to remove your records from {{ props.name }}
        </BaseText>
        <iframe
          ref="iframeRef"
          class="instructions"
          :srcdoc="markdownToHtml"
          :style="`height: ${state.iframeHeight}px; width: ${state.iframeWidth}px;`"
          frameborder="0"
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          @load="resizeIframe"
        />
        <BaseText
          v-if="showSupportLink"
          as="h3"
          variant="headline-4-bold"
        >
          {{ "Need more help removing your information? Click " }}
          <span
            class="link-text"
            @click="handleSupportLinkClick"
          >
            {{ "here" }}
          </span>
          {{ " to access a step by step walkthrough." }}
        </BaseText>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
/* Only keep specific styles unique to UiActionRow */
.action-row-details {
  gap: 16px;

  &.opened {
    margin-top: 16px;
  }

  .warning {
    background-color: $color-alert-tint;
    color: $color-alert;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
    width: 100%;
    border-radius: 16px;
  }

  ol {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    li {
      display: flex;
      flex-direction: row;
      gap: 15px;
      align-items: center;
      justify-content: flex-start;

      .list-rank {
        background-color: $color-primary-100;
        border-radius: 50%;
        color: $color-primary-1;
        height: 18px;
        width: 18px;
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .instructions {
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: $color-primary-70;
    width: 100%;
    // NOTE: li has margin-top: 16px;
    margin-top: -16px;

    iframe {
      border: none;
      width: 100%;
      height: auto;
    }
  }
}
</style>
